import { useState, useEffect } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Snackbar,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import DescriptionIcon from '@mui/icons-material/Description'
import { adminOrderService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'

const CreditNoteSettingsPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.CREDIT_NOTE_SETTINGS,
    'write',
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [percent, setPercent] = useState('')
  const [fieldError, setFieldError] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminOrderService.getPurchaseCreditSettings()
      const value = data?.purchase_credit_apply_percent
      setPercent(value !== undefined && value !== null ? String(value) : '80')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load credit note settings')
    } finally {
      setLoading(false)
    }
  }

  const validatePercent = (raw) => {
    const trimmed = String(raw).trim()
    if (trimmed === '') {
      return 'Apply percent is required'
    }
    const num = Number(trimmed)
    if (!Number.isInteger(num) || num < 0 || num > 100) {
      return 'Enter a whole number from 0 to 100'
    }
    return null
  }

  const handleSave = async () => {
    if (!canWrite) return
    const validationError = validatePercent(percent)
    if (validationError) {
      setFieldError(validationError)
      return
    }
    setFieldError(null)

    try {
      setSaving(true)
      setError(null)
      const num = Number(percent)
      const data = await adminOrderService.updatePurchaseCreditSettings(num)
      setPercent(String(data.purchase_credit_apply_percent))
      setSuccessMessage('Credit note settings saved')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save credit note settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <DescriptionIcon sx={{ color: '#e90000', fontSize: 32 }} />
        <Typography variant="h5" fontWeight={600}>
          Credit Note
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Credit notes are temporarily disabled. Settings cannot be changed until the
        feature is re-enabled.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Purchase credit apply percent
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Maximum share of a purchase order total that can be paid using available credit
            notes (0–100). This value is stored globally and applies to new purchase checkouts.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
              sx={{ maxWidth: 360 }}
            >
              <TextField
                fullWidth
                label="Apply percent (%)"
                type="number"
                value={percent}
                onChange={(e) => {
                  setPercent(e.target.value)
                  if (fieldError) setFieldError(null)
                }}
                error={!!fieldError}
                helperText={fieldError || 'Integer from 0 to 100'}
                inputProps={{ min: 0, max: 100, step: 1 }}
                disabled
              />
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled
                  sx={{
                    bgcolor: '#e90000',
                    '&:hover': { bgcolor: '#c40000' },
                  }}
                >
                  Save
                </Button>
                <Button variant="outlined" onClick={loadSettings} disabled={loading || saving}>
                  Reset
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  )
}

export default CreditNoteSettingsPage
